import styled from '@emotion/styled';
import Downshift from 'downshift';
import React, {
  type HTMLAttributes,
  useEffect,
  useState,
  useMemo,
} from 'react';

import { useLayer } from 'react-laag';
import ResizeObserver from 'resize-observer-polyfill';

import type { LensDivProps } from '../../types';
import { getColorValue, getSize, u } from '../../utilities';
import { unit } from '../../variables';

import { getDownshiftEnvironment } from '../../utilities/downshift';
import { getTextFromNode } from '../../utilities/text';
import { SvgChevronDown } from '../icon/available-icons/chevron-down';
import Icon from '../icon/icon';

import Menu, { MenuItem, menuPositions } from '../menu/menu';
import Align from '../align/align';
import Container from '../container/container';
import SearchBar from '../search-bar/search-bar';
import Spacer from '../spacer/spacer';
import Text from '../text/text';

// Reserve padding for side-positioned menus to prevent them from touching viewport edges
const SIDE_MENU_PADDING_UNITS = 2;

const SelectWrapper = styled.div`
  position: relative;
`;

const SelectHeaderWrapper = styled.button<SelectHeaderProps>`
  appearance: none;
  font: inherit;
  text-align: left;
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: ${props => props.columns};
  ${getSize('grid-gap', 'small')};
  align-items: center;
  cursor: pointer;
  width: 100%;
  min-height: ${u(4.5)};
  padding: 0 ${u(1.5)} 0 var(--lns-formFieldHorizontalPadding);
  color: ${getColorValue('body')};
  border: none;
  background-color: ${getColorValue('formFieldBackground')};
  transition: 0.3s box-shadow;
  border-radius: var(--lns-formFieldRadius);
  box-shadow: inset 0 0 0
    ${props =>
      props.hasError
        ? 'var(--lns-formFieldBorderWidthFocus) var(--lns-color-danger)'
        : 'var(--lns-formFieldBorderWidth) var(--lns-color-formFieldBorder)'};

  &:hover:not(:disabled) {
    box-shadow: inset 0 0 0 var(--lns-formFieldBorderWidthFocus)
      ${props =>
        props.hasError
          ? 'var(--lns-color-danger)'
          : 'var(--lns-color-blurple)'};
  }

  &:focus {
    outline: 1px solid transparent;
    box-shadow: var(--lns-formFieldBorderShadowFocus);
  }

  &:focus:hover {
    outline: 1px solid transparent;
    box-shadow: var(--lns-formFieldBorderShadowFocus);
  }

  &:disabled {
    color: ${getColorValue('disabledContent')};
    background-color: ${getColorValue('disabledBackground')};
    cursor: default;
  }
`;

// Scale down proportionally (preserving aspect ratio) and crop overflow
const Image = styled.img<Pick<SelectProps, 'isDisabled'>>`
  height: 100%;
  width: auto;
  min-width: 100%;
  min-height: 100%;
  object-fit: cover;
  opacity: ${({ isDisabled }) => (isDisabled ? 0.5 : 1)};
`;

const Group = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const ErrorMessage = styled.span`
  color: var(--lns-color-red);
  margin-top: var(--lns-space-xsmall);
  display: block;
  width: 100%;
  grid-column-start: 1;
  grid-column-end: 3;
`;

const isGrouped = (
  options: OptionsObject[] | GroupedOptionsObject[]
): options is GroupedOptionsObject[] =>
  Array.isArray(options) && options.length > 0 && 'group' in options[0];

const getSelectedOptionObject = ({ options, selectedOptionValue }) => {
  if (!options || !selectedOptionValue) {
    return { icon: null, title: null };
  }

  if (isGrouped(options)) {
    for (const option of options) {
      const foundItem = option.items.find(
        item => item.value === selectedOptionValue
      );
      if (foundItem) {
        return foundItem;
      }
    }
  } else {
    const foundItem = options.find(item => item.value === selectedOptionValue);
    return foundItem || { icon: null, title: null };
  }

  return { icon: null, title: null };
};

const getIcon = ({ options, selectedItem, selectedOptionValue }) => {
  if (selectedItem) {
    return selectedItem.icon;
  }

  if (selectedOptionValue) {
    return getSelectedOptionObject({ options, selectedOptionValue }).icon;
  }
};

const getTitle = ({
  options,
  selectedItem,
  selectedOptionValue,
  selectPlaceholder,
}) => {
  if (selectedItem) {
    return selectedItem.title;
  }

  if (selectedOptionValue) {
    return getSelectedOptionObject({ options, selectedOptionValue }).title;
  }

  return selectPlaceholder;
};

const getHeaderAccessibilityProps = ({
  selectedItem,
  getInputProps,
  getToggleButtonProps,
  ariaMenuName,
  isOpen,
}) => {
  const valueLabel = selectedItem
    ? `selected value is ${selectedItem.title}`
    : 'no value selected';

  const toggleLabel = getToggleButtonProps?.()['aria-label'];
  return {
    'aria-expanded': isOpen,
    // Ensures all options within the list of selections can be narrated by a screen reader
    'aria-activedescendant': getInputProps()['aria-activedescendant'],
    // The existing aria-label includes whether or not the menu is open or closed, and we add the valueLabel to make a screen reader narrate what is selected
    'aria-label': [ariaMenuName, toggleLabel, valueLabel]
      .filter(Boolean)
      .join(', '),
  };
};

const SelectHeader = ({
  getToggleButtonProps,
  inputValue,
  selectedItem,
  selectedOptionValue,
  selectPlaceholder,
  isDisabled,
  options,
  getInputProps,
  ariaMenuName,
  hasError,
  isOpen,
}: SelectHeaderProps & React.ComponentProps<typeof SelectHeaderWrapper>) => {
  const icon = getIcon({ options, selectedItem, selectedOptionValue });
  const hasIcon = Boolean(icon);

  const hasPlaceholder = !selectedOptionValue && !selectedItem;
  const iconColumn = hasIcon ? 'auto' : '';
  const columns = `${iconColumn} 1fr auto`;
  const color = isDisabled ? 'disabledContent' : undefined;

  return (
    <SelectHeaderWrapper
      {...getToggleButtonProps()}
      {...getHeaderAccessibilityProps({
        selectedItem,
        getInputProps,
        getToggleButtonProps,
        ariaMenuName,
        isOpen,
      })}
      hasValue={inputValue || selectedOptionValue}
      disabled={isDisabled}
      columns={columns}
      hasError={hasError}
    >
      {hasIcon &&
        (typeof icon === 'string' ? (
          <Container radius="50" width={3} height={3} overflow="hidden">
            <Align alignment="center">
              <Image src={icon} alt="" isDisabled={isDisabled} />
            </Align>
          </Container>
        ) : (
          <Icon
            icon={getIcon({ options, selectedItem, selectedOptionValue })}
            color={color}
          />
        ))}
      <Text hasEllipsis color={hasPlaceholder ? 'bodyDimmed' : 'inherit'}>
        {getTitle({
          options,
          selectedItem,
          selectedOptionValue,
          selectPlaceholder,
        })}
      </Text>
      <Icon icon={<SvgChevronDown />} color={color} />
    </SelectHeaderWrapper>
  );
};

const CustomHeader = ({
  selectedOptionValue,
  selectedItem,
  trigger,
  getToggleButtonProps,
  options,
  selectPlaceholder,
  isDisabled,
  getInputProps,
  ariaMenuName,
  hasError,
  errorMessage,
  isOpen,
}) => {
  const buttonProps = () => {
    return {
      ...getToggleButtonProps(),
      ...getHeaderAccessibilityProps({
        selectedItem,
        getInputProps,
        getToggleButtonProps,
        ariaMenuName,
        isOpen,
      }),
    };
  };
  const getSelectedItem = (): OptionsObject | undefined => {
    if (selectedItem) {
      return selectedItem;
    }

    if (selectedOptionValue) {
      return getSelectedOptionObject({ options, selectedOptionValue });
    }
  };

  const triggerContent = {
    ...getSelectedItem(),
    placeholder: selectPlaceholder,
    isDisabled,
    hasError,
    errorMessage,
  };

  return trigger(triggerContent, buttonProps());
};

const getSelectedOption = (
  value: string,
  options: OptionsObject[] | GroupedOptionsObject[]
): OptionsObject | undefined => {
  if (isGrouped(options)) {
    for (const option of options) {
      const foundItem = option.items.find(item => item.value === value);
      if (foundItem) {
        return foundItem;
      }
    }
  } else {
    return options.find(item => item.value === value);
  }
};

const renderOption = (
  item,
  index,
  selectedItem,
  selectedOptionValue,
  highlightedIndex,
  getItemProps,
  keyboardMove,
  setKeyboardMove,
  allowsDeselect = false
) => {
  const isSelected =
    (!selectedItem && item.value === selectedOptionValue) ||
    (selectedItem && selectedItem.value === item.value);
  return (
    <MenuItem
      key={index}
      getItemProps={getItemProps}
      icon={item.icon}
      hidden={item.hidden}
      {...getItemProps({
        key: `${item.value}-${index}`,
        index,
        item,
        disabled: item.isDisabled,
        'aria-selected': isSelected,
        onMouseMove: () => {
          if (keyboardMove) {
            setKeyboardMove(false);
          }
        },
      })}
      isDisabled={item.isDisabled}
      hasDivider={item.hasDivider}
      isHighlighted={highlightedIndex === index}
      keyboardMove={keyboardMove && highlightedIndex === index}
      isSelected={isSelected}
      showDeselectIcon={allowsDeselect && isSelected}
    >
      {item.title}
    </MenuItem>
  );
};

const SelectMenu = ({
  options,
  selectedItem,
  selectedOptionValue,
  highlightedIndex,
  getItemProps,
  search,
  keyboardMove,
  setKeyboardMove,
  allowsDeselect,
  ...menuProps
}) => {
  if (!isGrouped(options)) {
    return (
      <Menu search={search} {...menuProps}>
        {options.map((item, index) =>
          renderOption(
            item,
            index,
            selectedItem,
            selectedOptionValue,
            highlightedIndex,
            getItemProps,
            keyboardMove,
            setKeyboardMove,
            allowsDeselect
          )
        )}
      </Menu>
    );
  }

  let downshiftIndex = 0;
  return (
    <Menu search={search} {...menuProps}>
      {options.map(group => {
        const groupId = `group-${group.group.replace(/\s+/g, '-')}`;
        return (
          <li key={groupId}>
            <Spacer left="medium" top="small" bottom="xsmall">
              <Text id={groupId} size="body-sm" fontWeight="bold">
                {group.group}
              </Text>
            </Spacer>
            <Group role="group" aria-labelledby={groupId}>
              {group.items.map(item => {
                return renderOption(
                  item,
                  downshiftIndex++,
                  selectedItem,
                  selectedOptionValue,
                  highlightedIndex,
                  getItemProps,
                  keyboardMove,
                  setKeyboardMove,
                  allowsDeselect
                );
              })}
            </Group>
          </li>
        );
      })}
    </Menu>
  );
};

const Select = ({
  container,
  onChange,
  menuZIndex = 1100,
  menuMaxWidth,
  menuMaxHeight = 34,
  menuMinWidth,
  triggerOffset = 0,
  ariaMenuName,
  selectedOptionValue,
  onOuterClick,
  options,
  placeholder,
  menuPosition = 'left',
  isDisabled,
  onOpenChange,
  trigger,
  hasError,
  errorMessage = "Oops, that didn't work.",
  search,
  allowsDeselect = false,
  ...props
}: SelectProps &
  Omit<React.ComponentProps<typeof SelectWrapper>, keyof SelectProps> &
  Omit<
    LensDivProps,
    keyof SelectProps & React.ComponentProps<typeof SelectWrapper>
  >) => {
  const environment = getDownshiftEnvironment(container);

  const [downshiftIsOpen, setDownshiftIsOpen] = useState(false);
  const [keyboardMove, setKeyboardMove] = useState(false);

  const [searchValue, setSearchValue] = useState('');

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    // Call backend search callback if provided
    search?.onSearch?.(value);
  };

  const [selectedItem, setSelectedItem] = useState<OptionsObject | null>(
    getSelectedOption(selectedOptionValue, options) ?? null
  );

  const onDownshiftChange = item => {
    // If allowsDeselect is enabled and user clicks the already-selected item, deselect it
    const isCurrentlySelected =
      (selectedItem && item && item.value === selectedItem.value) ||
      (!selectedItem && item && item.value === selectedOptionValue);

    if (allowsDeselect && isCurrentlySelected) {
      setSelectedItem(null);
      setDownshiftIsOpen(false);
      // Cast to any because type says OptionsObject, but we pass null when allowsDeselect=true
      onChange && onChange(null as any);
    } else {
      setSelectedItem(item ?? null);

      // Only call onChange if we have a valid item
      if (item) {
        onChange && onChange(item);
      } else if (allowsDeselect) {
        // Cast to any because type says OptionsObject, but we pass null when allowsDeselect=true
        onChange && onChange(null as any);
      }
      // If no item and allowsDeselect is false, don't call onChange
    }
  };

  // Sync Downshift's isOpen state back to our component state
  const onStateChange = changes => {
    if (changes.isOpen !== undefined) {
      setDownshiftIsOpen(changes.isOpen);
    }
  };

  const downshiftProps = {
    itemToString: item => (item ? item.value : ''),
    onChange: onDownshiftChange,
    onStateChange,
    onOuterClick,
    environment,
    selectedItem,
    isOpen: downshiftIsOpen,
  };

  if (environment) {
    downshiftProps.environment = environment;
  }

  // [0] Determine if the menu is side-positioned
  // For side-positioned menus (leftTop, rightTop), disable auto-repositioning
  // so the menu stays fixed to the side and an internal scroll is created instead so that the position
  // is actually considered "locked"
  const isSideMenu = menuPosition === 'leftTop' || menuPosition === 'rightTop';

  const { layerProps, triggerProps, renderLayer, triggerBounds } = useLayer({
    isOpen: downshiftIsOpen,
    container,
    ResizeObserver,
    placement: menuPositions[menuPosition],
    auto: !isSideMenu,
    snap: !isSideMenu,
    triggerOffset,
  });

  // [1] Calculate max height for side menus based on available space from trigger to viewport bottom
  // This ensures the menu aligns with the trigger top and fits within the viewport
  const effectiveMenuMaxHeight = useMemo(() => {
    if (isSideMenu && typeof window !== 'undefined' && triggerBounds) {
      // [2] Calculate available vertical space from trigger top to viewport bottom
      const viewportHeight = window.innerHeight;
      const availableHeight = viewportHeight - triggerBounds.top;
      // [3] Reserve some space for padding/margin to prevent touching viewport edges
      const paddingReserve = SIDE_MENU_PADDING_UNITS * unit;
      const calculatedMaxHeight = Math.floor(
        Math.max(0, availableHeight - paddingReserve) / unit
      );

      return menuMaxHeight
        ? Math.min(menuMaxHeight as number, calculatedMaxHeight)
        : calculatedMaxHeight;
    }
    return menuMaxHeight;
  }, [isSideMenu, menuMaxHeight, triggerBounds]);

  useEffect(() => {
    const expectedSelectedItem =
      getSelectedOption(selectedOptionValue, options) ?? null;

    // Only update if the expected item is different from current state
    if (expectedSelectedItem?.value !== selectedItem?.value) {
      setSelectedItem(expectedSelectedItem);
    }
  }, [selectedOptionValue, options, selectedItem]);

  useEffect(() => {
    onOpenChange && onOpenChange(downshiftIsOpen);
    // Reset search value when dropdown closes
    if (!downshiftIsOpen) {
      setSearchValue('');
      // Call onSearch with empty string to reset backend results
      search?.onSearch?.('');
    }
    // Only depend on search?.onSearch to avoid re-running when search object reference changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downshiftIsOpen, onOpenChange, search?.onSearch]);

  const stateReducer = (state, changes) => {
    // Handle deselection when clicking on already-selected item: We have to bypass the default behaviour of downshift of selecting the item again so that we can "deselect"
    if (
      allowsDeselect &&
      changes.type === Downshift.stateChangeTypes.clickItem &&
      changes.selectedItem
    ) {
      const isCurrentlySelected =
        (state.selectedItem &&
          changes.selectedItem.value === state.selectedItem.value) ||
        (!state.selectedItem &&
          changes.selectedItem.value === selectedOptionValue);

      if (isCurrentlySelected) {
        // Return null to deselect instead of selecting again and close the menu
        return { ...changes, selectedItem: null, isOpen: false };
      }
    }

    if (changes.type === Downshift.stateChangeTypes.keyDownEscape) {
      return { isOpen: false };
    }

    return changes;
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp':
      case 'ArrowLeft':
      case 'ArrowRight':
      case 'Enter':
      case ' ':
      case 'Tab':
      case 'Escape':
        setKeyboardMove(true);
        break;
      default:
        break;
    }
  };

  // Filtering in Atlassian is based on if the searched value is a substring of the provided values at
  // any point of the string.
  // For example is the searched value is "a" and the provided values are "a", "ab", "b", and "ba", the
  // returned values will be "a", "ab", and "ba".
  options = useMemo(() => {
    if (search) {
      // If backend search is provided, skip client-side filtering and return options as-is
      // The parent component is responsible for updating the options based on search results
      if (search.onSearch) {
        return options;
      }

      // Client-side filtering
      // For each group, filter the items based on the search value and then remove the groups that have no items
      if (isGrouped(options)) {
        let filteredGroups;

        if (search.searchType === 'startsWith') {
          filteredGroups = options.map(group => ({
            ...group,
            items: group.items.filter(item =>
              getTextFromNode(item.title)
                .toLowerCase()
                .startsWith(searchValue.toLowerCase())
            ),
          }));
        } else {
          filteredGroups = options.map(group => ({
            ...group,
            items: group.items.filter(item =>
              getTextFromNode(item.title)
                .toLowerCase()
                .includes(searchValue.toLowerCase())
            ),
          }));
        }

        return filteredGroups.reduce((acc, group) => {
          if (group.items.length > 0) {
            acc.push(group);
          }
          return acc;
        }, []);
        // If the options are not grouped, filter the options based on the search value
      }
      if (search.searchType === 'startsWith') {
        return options.filter(option =>
          getTextFromNode(option.title)
            .toLowerCase()
            .startsWith(searchValue.toLowerCase())
        );
      }

      return options.filter(option =>
        getTextFromNode(option.title)
          .toLowerCase()
          .includes(searchValue.toLowerCase())
      );

      // If the search parameter is not provided, return the options as is
    }
    return options;
    // Only depend on search?.onSearch and search?.searchType to avoid re-computing when search object reference changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, searchValue, search?.onSearch, search?.searchType]);

  return (
    <SelectWrapper {...props}>
      <Downshift {...downshiftProps} stateReducer={stateReducer}>
        {({
          getItemProps,
          getInputProps,
          getMenuProps,
          getToggleButtonProps,
          isOpen,
          inputValue,
          highlightedIndex,
          selectedItem,
        }) => {
          return (
            // Don't set the role to combobox to ensure a consistent experience for screen readers
            <div role="presentation" aria-expanded={undefined}>
              <div {...triggerProps}>
                {trigger ? (
                  <CustomHeader
                    getToggleButtonProps={() => ({
                      ...getToggleButtonProps({ onKeyDown }),
                    })}
                    selectedItem={selectedItem}
                    selectedOptionValue={selectedOptionValue}
                    selectPlaceholder={placeholder}
                    isDisabled={isDisabled}
                    options={options}
                    trigger={trigger}
                    getInputProps={getInputProps}
                    ariaMenuName={ariaMenuName}
                    hasError={hasError}
                    errorMessage={errorMessage}
                    isOpen={isOpen}
                  />
                ) : (
                  <SelectHeader
                    getToggleButtonProps={() => ({
                      ...getToggleButtonProps({ onKeyDown }),
                    })}
                    selectedItem={selectedItem}
                    selectedOptionValue={selectedOptionValue}
                    selectPlaceholder={placeholder}
                    options={options}
                    inputValue={inputValue}
                    isDisabled={isDisabled}
                    getInputProps={getInputProps}
                    ariaMenuName={ariaMenuName}
                    hasError={hasError}
                    isOpen={isOpen}
                  />
                )}
              </div>
              {downshiftIsOpen &&
                isOpen &&
                renderLayer(
                  <div
                    {...layerProps}
                    style={{
                      ...layerProps.style,
                      zIndex: menuZIndex,
                      width: menuMinWidth ? 'auto' : triggerBounds?.width,
                    }}
                  >
                    <SelectMenu
                      options={options}
                      selectedItem={selectedItem}
                      selectedOptionValue={selectedOptionValue}
                      highlightedIndex={highlightedIndex}
                      getItemProps={getItemProps}
                      position={menuPosition}
                      downshiftMenuProps={() => getMenuProps({ onKeyDown })}
                      maxWidth={menuMaxWidth}
                      maxHeight={effectiveMenuMaxHeight}
                      minWidth={menuMinWidth}
                      search={
                        search && (
                          <SearchBar
                            ariaLabel={search.searchPlaceholder}
                            placeholder={search.searchPlaceholder}
                            value={searchValue}
                            onChange={onSearchChange}
                            getInputProps={getInputProps}
                          />
                        )
                      }
                      keyboardMove={keyboardMove}
                      setKeyboardMove={setKeyboardMove}
                      allowsDeselect={allowsDeselect}
                    />
                  </div>
                )}
              {hasError && !downshiftIsOpen && (
                <ErrorMessage>{errorMessage}</ErrorMessage>
              )}
            </div>
          );
        }}
      </Downshift>
    </SelectWrapper>
  );
};

export type OptionsObject = {
  value: string;
  title: React.ReactNode;
  icon?: React.ReactNode | string;
  isDisabled?: boolean;
  hasDivider?: boolean;
  hidden?: HTMLAttributes<HTMLOptionElement>['hidden'];
};

export type GroupedOptionsObject = {
  group: string;
  items: OptionsObject[];
};

type SearchProps = {
  searchPlaceholder: string;
  searchType?: 'contains' | 'startsWith';
  /**
   * Optional callback for backend search. When provided, client-side filtering is disabled
   * and this callback is called whenever the search input changes.
   * Use this to fetch search results from your backend API.
   */
  onSearch?: (searchValue: string) => void;
};

type SelectProps = {
  /**
   * Callback when selection changes.
   * - When allowsDeselect is NOT set (default): selectedOption is always an OptionsObject
   * - When allowsDeselect=true: selectedOption can be null when user deselects
   *
   * Note: The type shows only OptionsObject for backwards compatibility, but at runtime
   * null is passed when allowsDeselect=true. If using allowsDeselect, check for null.
   */
  onChange?: (selectedOption: OptionsObject) => void;
  isDisabled?: boolean;
  placeholder?: React.ReactNode;
  selectedOptionValue?: string | null;
  menuZIndex?: number;
  menuMaxWidth?: string | number;
  menuMaxHeight?: string | number;
  menuMinWidth?: string | number;
  menuPosition?:
    | 'left'
    | 'right'
    | 'topRight'
    | 'topLeft'
    | 'rightTop'
    | 'leftTop';
  triggerOffset?: number;
  ariaMenuName?: string;
  options?: OptionsObject[] | GroupedOptionsObject[];
  container?: HTMLElement | (() => HTMLElement | null);
  onOuterClick?: () => void;
  onOpenChange?: (isOpen: boolean) => void;
  trigger?: (triggerContent, buttonProps) => void;
  hasError?: boolean;
  errorMessage?: string;
  search?: SearchProps;
  /**
   * When true, clicking the selected item will deselect it and pass null to onChange.
   * When false/undefined, clicking the selected item does nothing.
   */
  allowsDeselect?: boolean;
};

type SelectHeaderProps = {
  getToggleButtonProps?: any;
  inputValue?: string;
  selectedItem?: { icon: React.ReactNode; title: string };
  selectedOptionValue?: string;
  selectPlaceholder?: React.ReactNode;
  isDisabled?: boolean;
  options?: OptionsObject[] | GroupedOptionsObject[];
  columns?: string;
  getInputProps?: any;
  ariaMenuName?: string;
  hasError?: boolean;
  isOpen?: boolean;
};

export default Select;
